import React, { useState } from "react";
import LegalHeader from "../components/LegalHeader";
import Footer from "../components/Footer";

export default function Disclaimer() {
  const [activeSection, setActiveSection] = useState<string>("intro");

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -90;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const sections = [
    { id: "intro", title: "1. General Legal Disclaimer" },
    { id: "intermediary", title: "2. Platform & Intermediary Status (Section 79 IT Act)" },
    { id: "contractors", title: "3. Independent Contractor Disclaimer" },
    { id: "appliance", title: "4. Pre-Existing Faults & Appliance Aging" },
    { id: "spare-parts", title: "5. Spare Parts & Manufacturer Warranties" },
    { id: "estimates", title: "6. Estimates vs Physical Diagnostic Scope" },
    { id: "emergency", title: "7. Non-Emergency Services Disclaimer" },
    { id: "valuables", title: "8. Safekeeping of Personal Valuables" },
    { id: "availability", title: "9. 'As-Is' Platform Availability" },
    { id: "third-parties", title: "10. External Links & Payment Gateways" },
  ];

  return (
    <div className="w-full bg-white min-h-screen flex flex-col font-['Roboto',sans-serif]">
      <LegalHeader />

      {/* Header Title Section */}
      <section className="border-b border-gray-200 py-10 px-6">
        <div className="max-w-[1100px] mx-auto">
          <p className="text-xs text-gray-500 mb-2">Fixezy / Legal / Disclaimer</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Disclaimer
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Last updated: October 2026 • Governing service limitations and safety disclosures
          </p>
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="max-w-[1100px] mx-auto px-6 py-10 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Table of Contents - Clean simple text links */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-28 border border-gray-200 rounded-lg p-5 bg-white">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">
              Table of Contents
            </p>
            <nav className="flex flex-col space-y-2">
              {sections.map((section) => (
                <p
                  key={section.id}
                  onClick={() => scrollTo(section.id)}
                  className={`cursor-pointer text-xs transition-colors ${activeSection === section.id
                      ? "text-black font-semibold"
                      : "text-gray-600 hover:text-black"
                    }`}
                >
                  {section.title}
                </p>
              ))}
            </nav>
          </aside>

          {/* Legal Text Area */}
          <main className="lg:col-span-8 text-gray-700 space-y-10 leading-relaxed text-sm sm:text-base">
            {/* Section 1 */}
            <section id="intro" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                1. General Legal Disclaimer
              </h2>
              <p>
                The information, pricing estimates, and service descriptions published on the Fixezy website (fixezy.in) and mobile applications (Android / iOS) are provided for general informational and home service arrangement purposes only. Your use of Fixezy is at your sole discretion, subject to the conditions set forth herein.
              </p>
            </section>

            {/* Section 2 */}
            <section id="intermediary" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                2. Platform & Intermediary Status (Section 79 IT Act)
              </h2>
              <p>
                Fixezy Private Limited functions as an intermediary under Section 2(w) of the Information Technology Act, 2000, and is protected under the statutory safe harbor provisions of Section 79 of the Information Technology Act, 2000. Fixezy provides a digital platform enabling communication, booking, and payment processing between customers and independent certified technicians.
              </p>
            </section>

            {/* Section 3 */}
            <section id="contractors" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                3. Independent Contractor Disclaimer
              </h2>
              <p>
                All service technicians (plumbers, electricians, appliance technicians, and cleaning specialists) listed on Fixezy are independent contractors and not direct employees or agents of Fixezy Private Limited. Fixezy verifies identity credentials (Aadhaar/PAN), conducts background checks, and assesses trade experience, but does not supervise the physical execution of work on-site.
              </p>
            </section>

            {/* Section 4 */}
            <section id="appliance" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                4. Pre-Existing Faults & Appliance Aging
              </h2>
              <p>
                Household appliances (air conditioners, refrigerators, washing machines, microwaves) are subject to material wear, corrosion, and manufacturer lifecycles:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1.5 text-gray-700">
                <li>
                  <strong>Latent Defects:</strong> Fixezy and its technicians are not responsible for pre-existing internal fractures, brittle aged plastics, or corroded copper coils that become apparent during standard diagnostic disassembly.
                </li>
                <li>
                  <strong>Aged Equipment:</strong> When an appliance has exceeded its normal manufacturer lifecycle or when proprietary spare parts are discontinued by the manufacturer, Fixezy cannot guarantee complete repairability. In such instances, only the diagnostic visit fee applies.
                </li>
                <li>
                  <strong>Prior Uncertified Repairs:</strong> Fixezy disclaims liability for cascading failures resulting from past improper modifications made by third-party local mechanics.
                </li>
              </ul>
            </section>

            {/* Section 5 */}
            <section id="spare-parts" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                5. Spare Parts & Manufacturer Warranties
              </h2>
              <p>
                When replacement spare parts are provided during repairs, any warranty on the part is governed exclusively by the original spare part manufacturer. Fixezy's 30-Day Revisit Guarantee covers workmanship and labor only. If a customer provides their own spare parts, Fixezy provides no warranty on the part's performance.
              </p>
            </section>

            {/* Section 6 */}
            <section id="estimates" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                6. Estimates vs Physical Diagnostic Scope
              </h2>
              <p>
                Any preliminary pricing or diagnostic information communicated over the phone or in promotional material is an indicative estimate based on user-described symptoms. The actual job scope, required spare parts, and final price are determined only after the technician completes a physical on-site diagnostic inspection and issues a digital job sheet for your approval.
              </p>
            </section>

            {/* Section 7 - Critical Emergency Warning */}
            <section id="emergency" className="scroll-mt-28 border-t border-gray-200 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                7. Non-Emergency Services Disclaimer
              </h2>
              <p className="font-medium text-gray-900">
                Fixezy is an on-demand maintenance and home service platform. We are NOT an emergency response provider or disaster rescue service.
              </p>
              <div className="mt-3 border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-2 text-sm text-gray-800">
                <p>In hazardous emergency situations:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Gas Leaks (LPG / PNG):</strong> Immediately turn off the main cylinder valve, evacuate the premises, and call the National Gas Emergency Helpline at <strong>1906</strong>.</li>
                  <li><strong>Electrical Fire or Heavy Smoke:</strong> Disconnect the main circuit breaker / MCB and dial Fire Emergency at <strong>101</strong>.</li>
                  <li><strong>Structural Collapse or Severe Flooding:</strong> Dial National Emergency Services at <strong>112</strong>.</li>
                </ul>
                <p className="text-xs text-gray-500 pt-1">Do not rely on Fixezy bookings for urgent life-safety emergencies.</p>
              </div>
            </section>

            {/* Section 8 */}
            <section id="valuables" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                8. Safekeeping of Personal Valuables
              </h2>
              <p>
                Customers are advised to lock away and secure all cash, jewelry, valuables, gadgets, and sensitive documents prior to technician arrival. While all Service Partners undergo identity and background checks, Fixezy is not liable for loss or misplacement of unattended personal property.
              </p>
            </section>

            {/* Section 9 */}
            <section id="availability" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                9. 'As-Is' Platform Availability
              </h2>
              <p>
                The Fixezy application and website are provided on an "as-is" and "as-available" basis. While we strive for continuous uptime, Fixezy does not guarantee uninterrupted or error-free access and is not liable for telecommunication outages or third-party server downtimes.
              </p>
            </section>

            {/* Section 10 */}
            <section id="third-parties" className="scroll-mt-28 border-t border-gray-200 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                10. External Links & Payment Gateways
              </h2>
              <p>
                Our platform integrates with RBI-regulated third-party payment gateways (e.g., UPI, Netbanking). Fixezy is not liable for technical delays or failed bank transaction settlement issues governed by external banking systems.
              </p>
              <p className="mt-3 text-sm text-gray-600">
                For questions regarding this Disclaimer, contact: <strong>support@fixezy.in</strong>.
              </p>
            </section>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
